/**
 * Enemy archetype registry — typed data the StageScene reads to spawn distinct
 * enemy types from the one generic Enemy class. New archetypes (Albanian
 * smuggler, bouncer, drill-rap shotcaller, …) slot in here per their tickets.
 *
 * Tiers (Streets-of-Rage lineage):
 *   galsia  — basic mob (low HP, simple punch)
 *   donovan — mid (blocks, combos, back-steps)
 *   signal  — ranged (throws/projectiles)
 *   elite   — tough sub-boss-tier
 *   boss    — stage boss with phase-based AI (BargeeAI, etc.)
 */
import type { AnimState } from '@/systems/AnimationStateMachine';
import mopedSnatcherJson from './enemies/moped-snatcher.json';
import downedMopedRiderJson from './enemies/downed-moped-rider.json';
import dogHandlerJson from './enemies/dog-handler.json';
import attackDogJson from './enemies/attack-dog.json';
import juggernautJson from './enemies/juggernaut.json';
import bentCopperJson from './enemies/bent-copper.json';
import countyRunnerJson from './enemies/county-runner.json';
import countyEnforcerJson from './enemies/county-enforcer.json';
import bailiffHeavyJson from './enemies/bailiff-heavy.json';
import provocateurJson from './enemies/provocateur.json';
import sohoHeadhunterJson from './enemies/soho-headhunter.json';
import dockForemanJson from './enemies/dock-foreman.json';
import railWardenJson from './enemies/rail-warden.json';
import cadreCaptainJson from './enemies/cadre-captain.json';
import beachMasterJson from './enemies/beach-master.json';
import floorChiefJson from './enemies/floor-chief.json';
import praetorianCaptainJson from './enemies/praetorian-captain.json';
import outriderBikerJson from './enemies/outrider-biker.json';
import downedRiderJson from './enemies/downed-rider.json';
import albanianSmugglerJson from './enemies/albanian-smuggler.json';
import theBargeeJson from './enemies/the-bargee.json';
import sohoToutJson from './enemies/soho-tout.json';
import clubBouncerJson from './enemies/club-bouncer.json';
import doormanRexJson from './enemies/doorman-rex.json';
import crateRunnerJson from './enemies/crate-runner.json';
import dockyardHeavyJson from './enemies/dockyard-heavy.json';
import theFerrymanJson from './enemies/the-ferryman.json';
import platformToughJson from './enemies/platform-tough.json';
import trackRatHandlerJson from './enemies/track-rat-handler.json';
import theConductorJson from './enemies/the-conductor.json';
import spicedEnforcerJson from './enemies/spiced-enforcer.json';
import labMuscleJson from './enemies/lab-muscle.json';
import theChemistJson from './enemies/the-chemist.json';
import cadreAgitatorJson from './enemies/cadre-agitator.json';
import cadreMarshalJson from './enemies/cadre-marshal.json';
import comradeVexJson from './enemies/comrade-vex.json';
import ribSmugglerJson from './enemies/rib-smuggler.json';
import cliffSpotterJson from './enemies/cliff-spotter.json';
import captainVladJson from './enemies/captain-vlad.json';
import tradingFloorGoonJson from './enemies/trading-floor-goon.json';
import corporateSecurityJson from './enemies/corporate-security.json';
import theHedgeFundJson from './enemies/the-hedge-fund.json';
import manorPraetorianJson from './enemies/manor-praetorian.json';
import whitehallFixerJson from './enemies/whitehall-fixer.json';
import hallowayAstorJson from './enemies/halloway-astor.json';
import transitVanJson from './enemies/transit-van.json';
import ebikeCourierJson from './enemies/ebike-courier.json';
import thePraetorJson from './enemies/the-praetor.json';
import riotSergeantJson from './enemies/riot-sergeant.json';

export type EnemyTier = 'galsia' | 'donovan' | 'signal' | 'elite' | 'boss';

/**
 * Returns the default light-poise threshold for a tier (MY-2414).
 * Enemies shrug off hitstun after this many consecutive light hits.
 * Individual archetypes may override via `lightPoiseThreshold` in their JSON.
 */
export function getDefaultPoiseThreshold(tier: EnemyTier): number {
  const defaults: Record<EnemyTier, number> = {
    galsia: 3,
    donovan: 4,
    signal: 4,
    elite: 6,
    boss: 8,
  };
  return defaults[tier];
}

export interface EnemyDef {
  id: string;
  name: string;
  tier: EnemyTier;
  maxHp: number;
  /** Placeholder body colour until sprite art lands. */
  color: number;
  /** Pixels/frame at 60fps. */
  speed: number;
  bodyW: number;
  bodyH: number;
  /** 0..1 chance to drop a chips (heal) pickup on KO. */
  chipDropChance: number;
  /** Occasionally lobs a thrown object (produce) at range. */
  rangedThrow: boolean;
  // --- donovan-tier traits ---
  /** Absorbs player light-attack hits (chip damage only, no full hitstun). */
  blocksLight?: boolean;
  /** Player can parry this enemy's melee attacks. */
  parryable?: boolean;
  /** Number of attacks per combo sequence (default 1 for galsia-tier). */
  comboLength?: number;
  /** Executes a quick back-step after completing a combo. */
  hasBackStep?: boolean;
  /** Skips standard hitstun on light hits (elite-tier trait). Combat system must honour this. */
  ignoresHitstun?: boolean;
  // --- behaviour variant flags (MY-1798) ---
  /** Closes to grab player; player mashes to escape. */
  grabber?: boolean;
  /** Leaps to close distance or over player. */
  jumper?: boolean;
  /** Kamikaze rush then self-destruct AoE. */
  bomber?: boolean;
  /** Blocks frontal hits; must be flanked or grabbed. */
  shielded?: boolean;
  /** Keeps distance; uses ranged attacks. */
  sniper?: boolean;
  // --- biker variant (MY-1800) ---
  /** Mounted motorbike enemy; strafes across the lane. */
  biker?: boolean;
  /** Hits while mounted before the rider is knocked off (default: 3). */
  bikerKnockOffHits?: number;
  // --- moped snatcher (MY-1910) ---
  /** Two-up moped snatch-crew; rides in, pillion grabs item or player, flees. */
  mopedSnatcher?: boolean;
  /** Hits while mounted before rider(s) are knocked off (default: 3). */
  mopedSnatchKnockOffHits?: number;
  // --- armour variant (MY-1886) ---
  /**
   * Initial armour HP pool. Must be depleted before HP takes damage.
   * Heavy hits (damage >= HEAVY_HIT_THRESHOLD) and throws drain at full rate;
   * light hits chip at LIGHT_ARMOUR_FRACTION. High poise until armour breaks.
   */
  armourPool?: number;
  // --- group tactics (MY-1887) ---
  /**
   * Enable gang-tactics layer: ≥3 attackers spread to surround slots (flanks +
   * behind); lone low-HP mob backs off and re-approaches instead of feeding.
   */
  groupTactics?: boolean;
  // --- attack-dog handler pair (MY-1888) ---
  /**
   * Handler that releases an attack dog; retreats and re-summons after
   * DOG_HANDLER_RESUMMON_COOLDOWN when the dog is KO'd.
   * Driven by DogHandlerAI (src/systems/DogHandlerAI.ts).
   */
  dogHandler?: boolean;
  /**
   * Fast, fragile attack dog released by the handler; lunges at the nearest
   * hero and re-targets on target loss.
   * Driven by AttackDogAI (src/systems/AttackDogAI.ts).
   */
  attackDog?: boolean;
  // --- transit van set-piece (MY-1889) ---
  /** Transit van: drives into the lane, deploys a mob squad, explodes on destroy. */
  transitVan?: boolean;
  // --- PM Halloway coda (MY-1978) ---
  /** Weak post-Praetor cowering entity. One hit KOs; deals no damage. */
  pmHalloway?: boolean;
  // --- standard mob grab (MY-1961) ---
  /**
   * Standard mob can attempt an opportunistic grab + throw on a grounded,
   * non-invulnerable player. Uses GRAB_WINDUP_FRAMES telegraph so the player
   * can react (dodge or hit the enemy out of it). Player mashes to shorten /
   * escape the hold (GrabEscapeGame). Falls back to a normal punch when the
   * player is airborne or invulnerable.
   * Handled by tickCanGrabVariant inside EnemyAI (src/systems/EnemyAI.ts).
   */
  canGrab?: boolean;
  // --- e-bike courier (MY-1911) ---
  /**
   * Fast lane-weaving flanker. Approaches with Y-oscillation, lobs a parcel /
   * bike-lock at COURIER_THROW_RANGE, then darts away.
   * Driven by EbikeCourierAI (src/systems/EbikeCourierAI.ts).
   */
  ebikeCourier?: boolean;
  /** Enemy archetype ids spawned as a one-shot burst when the van arrives. */
  vanSquadIds?: string[];
  // --- elite drop traits ---
  /** Spawns a weapon pickup at the KO location (elite-tier trait). */
  weaponDropOnKO?: boolean;
  /**
   * Override the tier-default light-poise threshold (MY-2414).
   * If absent, falls back to getDefaultPoiseThreshold(tier).
   * Enemies shrug off hitstun after this many consecutive light hits.
   */
  lightPoiseThreshold?: number;
  /** Visual scale multiplier — set by variant resolver (1.0 = base size). Consumed by rendering layer. */
  scale?: number;
  /** Damage multiplier — set by variant resolver (1.0 = base damage). Consumed by combat system. */
  dmgMul?: number;
  /** Sprite atlas assetId — renders the entity texture when loaded; falls back to colour body. */
  assetId?: string;
  /**
   * Per-state pose overrides (MY-1951). When a state key is present and the
   * assetId is loaded in the SpriteAtlas, that texture replaces the base sprite.
   * Composes with procedural locomotion transforms.
   */
  poseAssetIds?: Partial<Record<AnimState, string>>;
  /**
   * Taunt pose asset IDs (MY-1960). 1–2 per archetype; the Phaser adapter picks
   * one at random when the taunt animation plays. Falls back to the base assetId
   * if absent.
   */
  tauntAssetIds?: string[];
}

// ─── Faction archetypes (MY-1785) ─────────────────────────────────────────

/**
 * Corrupt police officer on Astor-Pemberton's payroll.
 * Donovan-tier: blocks light attacks, 2-hit combo, back-steps after finishing.
 */
export const BENT_COPPER: EnemyDef = bentCopperJson as EnemyDef;

/** County-lines drug courier. Galsia-tier fast runner. */
export const COUNTY_RUNNER: EnemyDef = countyRunnerJson as EnemyDef;

/** County-lines muscle. Donovan-tier: blocks light attacks, 2-hit combo. */
export const COUNTY_ENFORCER: EnemyDef = countyEnforcerJson as EnemyDef;

/**
 * Debt-collector enforcer sent by Astor-Pemberton's finance network.
 * Elite-tier: high HP, ignores hitstun, drops weapon on KO.
 */
export const BAILIFF_HEAVY: EnemyDef = bailiffHeavyJson as EnemyDef;

/**
 * Paid agitator embedded in protest crowds to escalate unrest.
 * Signal-tier: keeps distance, ranged throw (leaflets / flares), sniper flag.
 * Slots into Stage 6 (Bethnal Green) to pay off the "elites manufacture riots" reveal.
 */
export const PROVOCATEUR: EnemyDef = provocateurJson as EnemyDef;

/** Outrider gang biker. Strafes across the lane; rider knocked off after 3 hits. */
export const OUTRIDER_BIKER: EnemyDef = outriderBikerJson as EnemyDef;

/**
 * Grounded rider after being knocked off the bike.
 * Spawned by the Phaser adapter when wantsKnockOff fires (MY-1800).
 */
export const DOWNED_RIDER: EnemyDef = downedRiderJson as EnemyDef;

/** Stage 1 Camden basic mob. */
export const CAMDEN_TRADER: EnemyDef = {
  id: 'camden-trader',
  name: 'Camden Trader',
  tier: 'galsia',
  maxHp: 13,
  color: 0x8a6d3b,
  speed: 1.7,
  bodyW: 54,
  bodyH: 108,
  assetId: 'camden-trader',
  chipDropChance: 0.25,
  rangedThrow: true,
};

/** Stage 1 Camden mid-tier. Knife-vested, 2-hit combo, blocks light attacks. */
export const ALBANIAN_SMUGGLER: EnemyDef = albanianSmugglerJson as EnemyDef;

/**
 * Stage 1 boss. Three-phase canal barge fight — boat-hook lunge, hook-pull,
 * barrel-throw; summons 2 deckhands at 50% HP; cleat-swing arc at 20% HP.
 * Driven by BargeeAI (src/systems/BargeeAI.ts).
 * TODO(MY-1448): Wire BargeeEnemy Phaser adapter (extends Enemy, calls
 * tickBargeeAI, handles wantsSummonDeckhands to spawn deckhand wave).
 */
export const THE_BARGEE: EnemyDef = theBargeeJson as EnemyDef;

/** Stage 2 Soho basic mob. Fast galsia-tier tout; rushes in quick combos. */
export const SOHO_TOUT: EnemyDef = sohoToutJson as EnemyDef;

/** Stage 2 Soho mid-tier. Blocks light attacks, 2-hit combo. */
export const CLUB_BOUNCER: EnemyDef = clubBouncerJson as EnemyDef;

/**
 * Stage 2 boss. Three-phase nightclub door fight — velvet-rope lunge,
 * headbutt charge, crowd-scatter summon at 50% HP.
 * TODO(MY-1666): Wire DoormanRexEnemy Phaser adapter once boss AI lands.
 */
export const DOORMAN_REX: EnemyDef = doormanRexJson as EnemyDef;

/** Stage 3 Bermondsey Docks basic mob. Hurls cargo crates at range. */
export const CRATE_RUNNER: EnemyDef = crateRunnerJson as EnemyDef;

/** Stage 3 Bermondsey Docks mid-tier. Slow but tough; blocks light attacks. */
export const DOCKYARD_HEAVY: EnemyDef = dockyardHeavyJson as EnemyDef;

/**
 * Stage 3 boss. Three-phase container-yard fight — chain-swing lunge,
 * forklift charge, smuggler-summon at 50% HP; ranged crate lob at 20% HP.
 * TODO(MY-1667): Wire FerrymanEnemy Phaser adapter once boss AI lands.
 */
export const THE_FERRYMAN: EnemyDef = theFerrymanJson as EnemyDef;

/** Stage 4 Northern Line galsia-tier commuter tough. Sodium-amber palette. */
export const PLATFORM_TOUGH: EnemyDef = platformToughJson as EnemyDef;

/** Stage 4 Northern Line signal-tier; sics rats (ranged throw). */
export const TRACK_RAT_HANDLER: EnemyDef = trackRatHandlerJson as EnemyDef;

/**
 * Stage 4 boss. Tube conductor — baton swings, whistle stun, summons
 * platform-toughs at 50% HP; horn-blast area attack at 20% HP.
 * TODO(MY-1668): Wire ConductorEnemy Phaser adapter once boss AI lands.
 */
export const THE_CONDUCTOR: EnemyDef = theConductorJson as EnemyDef;

/** Stage 5 Croydon Spice Lab elite. Erratic lunges, high damage, ignores light hitstun. */
export const SPICED_ENFORCER: EnemyDef = spicedEnforcerJson as EnemyDef;

/** Stage 5 Croydon Spice Lab donovan-tier. Blocks light attacks, 2-hit combo. */
export const LAB_MUSCLE: EnemyDef = labMuscleJson as EnemyDef;

/**
 * Stage 5 boss. Lab kingpin — acid-vial lob, chemical-smoke area, summons
 * spiced-enforcers at 50% HP; beaker-smash melee at 20% HP.
 * TODO(MY-1669): Wire ChemistEnemy Phaser adapter once boss AI lands.
 */
export const THE_CHEMIST: EnemyDef = theChemistJson as EnemyDef;

/** Stage 6 Bethnal Green galsia-tier. Fast Crimson Cadre swarmer. */
export const CADRE_AGITATOR: EnemyDef = cadreAgitatorJson as EnemyDef;

/** Stage 6 Bethnal Green donovan-tier. Riot-shield bearer; blocks light attacks. */
export const CADRE_MARSHAL: EnemyDef = cadreMarshalJson as EnemyDef;

/**
 * Stage 6 boss. Crimson Cadre organiser — leaflet-lob ranged, baton charge,
 * summons cadre-agitators at 50% HP; megaphone-blast area at 20% HP.
 * TODO(MY-1670): Wire ComradeVexEnemy Phaser adapter once boss AI lands.
 * TODO(MY-1670): Astor-Pemberton end cutscene deferred to Stage 6 cutscene ticket.
 */
export const COMRADE_VEX: EnemyDef = comradeVexJson as EnemyDef;

/** Stage 7 Dover Coast galsia-tier. Armed smuggling crew aboard RIBs; rushes the shoreline. */
export const RIB_SMUGGLER: EnemyDef = ribSmugglerJson as EnemyDef;

/** Stage 8 Canary Wharf galsia-tier. Astor-Pemberton's hired trading-floor muscle. */
export const TRADING_FLOOR_GOON: EnemyDef = tradingFloorGoonJson as EnemyDef;

/** Stage 8 Canary Wharf donovan-tier. Tactical private security; blocks light attacks. */
export const CORPORATE_SECURITY: EnemyDef = corporateSecurityJson as EnemyDef;

/** Stage 7 Dover Coast signal-tier. Cliff-top lookout; hurls signal flares at range. */
export const CLIFF_SPOTTER: EnemyDef = cliffSpotterJson as EnemyDef;

/**
 * Stage 8 boss. Canary Wharf financier — briefcase-throw ranged, credit-card
 * swipe melee, summons corporate-security at 50% HP; market-crash area blast at 20% HP.
 * TODO(MY-1672): Wire HedgeFundEnemy Phaser adapter once boss AI lands.
 * TODO(MY-1672): lift-climb vertical-ascent scroll wired in StageScene follow-up.
 */
export const THE_HEDGE_FUND: EnemyDef = theHedgeFundJson as EnemyDef;

/**
 * Stage 7 boss. RIB fleet commander — hook-throw ranged, boarding-charge lunge,
 * summons rib-smugglers at 50% HP; storm-wave slam area at 20% HP.
 * TODO(MY-1671): Wire CaptainVladEnemy Phaser adapter once boss AI lands.
 * TODO(MY-1671): intercept-ribs objective full polish deferred to follow-up ticket.
 */
export const CAPTAIN_VLAD: EnemyDef = captainVladJson as EnemyDef;

/** Stage 9 Westminster elite. Halloway's private state-security muscle; high HP, 3-hit combo, ignores hitstun. */
export const MANOR_PRAETORIAN: EnemyDef = manorPraetorianJson as EnemyDef;

/** Stage 9 Westminster signal-tier. Whitehall operative; lobs signal devices at range. */
export const WHITEHALL_FIXER: EnemyDef = whitehallFixerJson as EnemyDef;

/**
 * Stage 9 final boss. Halloway & Astor tag-team — coordinated combo charges,
 * signal-device ranged lobs, summons manor-praetorians at 50% HP; joint area
 * blast at 20% HP.
 * TODO(MY-1673): Wire HallowayAstorEnemy Phaser adapter once boss AI lands.
 */
export const HALLOWAY_ASTOR: EnemyDef = hallowayAstorJson as EnemyDef;

/**
 * Stage 9 Phase 3 — THE PRAETOR, final boss (MY-1977).
 * Commander of MANOR's Praetorian Guard; hardest human fight.
 * Shield + baton, summons adds at 50% HP, enrages at 25%.
 * Driven by PraetorAI (src/systems/PraetorAI.ts).
 * Scored by music B2 "The Establishment".
 * TODO(MY-1977): Wire PraetorEnemy Phaser adapter (extends BossEnemy, calls
 * tickPraetorAI, honours guardActive for chip-damage on frontal hits,
 * handles wantsSummonAdds to spawn 2 manor-praetorian adds).
 */
export const THE_PRAETOR: EnemyDef = thePraetorJson as EnemyDef;

/**
 * Stage 9 coda — PM Halloway exposed after THE PRAETOR falls (MY-1978).
 * One-hit KO non-boss entity with cowering/fleeing AI.
 */
export const PM_HALLOWAY: EnemyDef = {
  id: 'pm-halloway',
  name: 'PM Halloway',
  tier: 'galsia',
  maxHp: 1,
  color: 0xd8c3a5,
  speed: 1.0,
  bodyW: 52,
  bodyH: 106,
  chipDropChance: 0,
  rangedThrow: false,
  pmHalloway: true,
  // Bespoke cowering weak-PM pixel sprite (RetroDiffusion text2img, MY-1978-art).
  assetId: 'pm-halloway',
};

// --- Stage 2–9 elite mini-bosses (MY-1782) ---

/** Stage 2 Soho elite. Fast nightclub enforcer; ignores light hitstun, drops weapon on KO. */
export const SOHO_HEADHUNTER: EnemyDef = sohoHeadhunterJson as EnemyDef;

/** Stage 3 Bermondsey Docks elite. Heavy union foreman; ignores light hitstun, drops weapon on KO. */
export const DOCK_FOREMAN: EnemyDef = dockForemanJson as EnemyDef;

/** Stage 4 Northern Line elite. TfL enforcement warden; ignores light hitstun, drops weapon on KO. */
export const RAIL_WARDEN: EnemyDef = railWardenJson as EnemyDef;

/** Stage 6 Bethnal Green elite. Crimson Cadre field commander; ignores light hitstun, drops weapon on KO. */
export const CADRE_CAPTAIN: EnemyDef = cadreCaptainJson as EnemyDef;

/** Stage 7 Dover Coast elite. Smuggling operation overseer; ignores light hitstun, drops weapon on KO. */
export const BEACH_MASTER: EnemyDef = beachMasterJson as EnemyDef;

/** Stage 8 Canary Wharf elite. Astor-Pemberton trading floor chief; ignores light hitstun, drops weapon on KO. */
export const FLOOR_CHIEF: EnemyDef = floorChiefJson as EnemyDef;

/** Stage 9 Westminster elite. Halloway's senior praetorian officer; heavily buffed praetorian, drops weapon on KO. */
export const PRAETORIAN_CAPTAIN: EnemyDef = praetorianCaptainJson as EnemyDef;

/**
 * Armoured bruiser (Andore-style). Armour pool must be depleted before HP
 * takes damage. High poise until armour breaks, then stagger-able.
 * Driven by standard EnemyAI + JuggernautArmour system (MY-1886).
 */
export const JUGGERNAUT: EnemyDef = juggernautJson as EnemyDef;

/**
 * Moped snatch-crew (MY-1910). Two-up moped: driver + pillion grabber.
 * Rides in, pillion snatches a dropped pickup or grabs the player, then flees.
 * Knock the rider(s) off to recover the stolen item.
 * Driven by MopedSnatcherAI (src/systems/MopedSnatcherAI.ts).
 */
export const MOPED_SNATCHER: EnemyDef = mopedSnatcherJson as EnemyDef;

/**
 * Grounded rider after being knocked off the moped.
 * Spawned by the Phaser adapter when wantsKnockOff fires (MY-1910).
 */
export const DOWNED_MOPED_RIDER: EnemyDef = downedMopedRiderJson as EnemyDef;

/**
 * Attack-dog handler (MY-1888). Donovan-tier: holds back while the dog
 * engages; retreats and calls a fresh dog after HANDLER_RESUMMON_COOLDOWN
 * when the dog is KO'd. Driven by DogHandlerAI.
 */
export const DOG_HANDLER: EnemyDef = dogHandlerJson as EnemyDef;

/**
 * Attack dog released by the handler (MY-1888). Fast galsia-tier: lunges at
 * the nearest hero and re-targets on target loss. Driven by AttackDogAI.
 */
export const ATTACK_DOG: EnemyDef = attackDogJson as EnemyDef;

/**
 * Destructible transit van set-piece (MY-1889). Drives into the lane, stops,
 * and deploys a squad of mobs (one burst via vanSquadIds). Solid obstacle with
 * 50 HP; destroying it awards VAN_DESTROY_SCORE + triggers an explosion that
 * knocks back and damages nearby enemies. Driven by TransitVanAI.
 */
export const TRANSIT_VAN: EnemyDef = transitVanJson as EnemyDef;

/**
 * E-bike courier flanker (MY-1911). Fast, lane-weaving signal-tier enemy.
 * Appearance: cycle helmet + neck-gaiter/buff + bulky thermal delivery backpack.
 * Closes to flank range, lobs a parcel / bike-lock (rangedThrow), then sprints
 * away. Low HP; high evasion via Y-oscillation weave.
 * Driven by EbikeCourierAI (src/systems/EbikeCourierAI.ts).
 */
export const EBIKE_COURIER: EnemyDef = ebikeCourierJson as EnemyDef;

/**
 * Riot Sergeant elite (MY-1885). Riot-control officer advancing behind a raised
 * riot shield. Frontal light hits are blocked; must be hit from the side/back
 * or have the guard broken by a HEAVY hit or throw. Periodically rallies nearby
 * mobs, granting them a brief aggression + speed buff.
 * Driven by RiotSergeantAI (src/systems/RiotSergeantAI.ts).
 */
export const RIOT_SERGEANT: EnemyDef = riotSergeantJson as EnemyDef;

export const ENEMIES: Record<string, EnemyDef> = {
  'bent-copper': BENT_COPPER,
  'county-runner': COUNTY_RUNNER,
  'county-enforcer': COUNTY_ENFORCER,
  'bailiff-heavy': BAILIFF_HEAVY,
  provocateur: PROVOCATEUR,
  'outrider-biker': OUTRIDER_BIKER,
  'downed-rider': DOWNED_RIDER,
  'camden-trader': CAMDEN_TRADER,
  'albanian-smuggler': ALBANIAN_SMUGGLER,
  'the-bargee': THE_BARGEE,
  'soho-tout': SOHO_TOUT,
  'club-bouncer': CLUB_BOUNCER,
  'doorman-rex': DOORMAN_REX,
  'crate-runner': CRATE_RUNNER,
  'dockyard-heavy': DOCKYARD_HEAVY,
  'the-ferryman': THE_FERRYMAN,
  'platform-tough': PLATFORM_TOUGH,
  'track-rat-handler': TRACK_RAT_HANDLER,
  'the-conductor': THE_CONDUCTOR,
  'spiced-enforcer': SPICED_ENFORCER,
  'lab-muscle': LAB_MUSCLE,
  'the-chemist': THE_CHEMIST,
  'cadre-agitator': CADRE_AGITATOR,
  'cadre-marshal': CADRE_MARSHAL,
  'comrade-vex': COMRADE_VEX,
  'rib-smuggler': RIB_SMUGGLER,
  'cliff-spotter': CLIFF_SPOTTER,
  'captain-vlad': CAPTAIN_VLAD,
  'trading-floor-goon': TRADING_FLOOR_GOON,
  'corporate-security': CORPORATE_SECURITY,
  'the-hedge-fund': THE_HEDGE_FUND,
  'manor-praetorian': MANOR_PRAETORIAN,
  'whitehall-fixer': WHITEHALL_FIXER,
  'halloway-astor': HALLOWAY_ASTOR,
  'soho-headhunter': SOHO_HEADHUNTER,
  'dock-foreman': DOCK_FOREMAN,
  'rail-warden': RAIL_WARDEN,
  'cadre-captain': CADRE_CAPTAIN,
  'beach-master': BEACH_MASTER,
  'floor-chief': FLOOR_CHIEF,
  'praetorian-captain': PRAETORIAN_CAPTAIN,
  juggernaut: JUGGERNAUT,
  'moped-snatcher': MOPED_SNATCHER,
  'downed-moped-rider': DOWNED_MOPED_RIDER,
  'dog-handler': DOG_HANDLER,
  'attack-dog': ATTACK_DOG,
  'transit-van': TRANSIT_VAN,
  'ebike-courier': EBIKE_COURIER,
  'the-praetor': THE_PRAETOR,
  'pm-halloway': PM_HALLOWAY,
  'riot-sergeant': RIOT_SERGEANT,
};

/** Look up an archetype by id; falls back to the Camden Trader. */
export function getEnemyDef(id: string): EnemyDef {
  return ENEMIES[id] ?? CAMDEN_TRADER;
}
